<template>
    <div :class="{ shadow: isPopupOpen }"
         :style="{ marginBottom: '10px' }">
        <div class="table-row camp-row">
            <div class="td order-0 order-lg-0">
                <div class="tools">
                    <div v-if="!readonly && accessibleItems.length"
                         class="checkbox-wrapper">
                        <checkbox v-select="campId"
                                  :checked="checked">
                        </checkbox>
                    </div>
                    <div  v-if="showMobileVersion"
                          class="d-flex justify-content-between align-items-center">
                        <img v-if="pricings.length"
                             src="../../../../assets/img/opened.svg"
                             :class="['down-arrow', isPopupOpen ? 'rotatedBack': 'rotated']"
                             @click="togglePopup">
                        <options class="ml-auto"
                                 v-if="!readonly"
                                 :options="accessibleItems">
                        </options>
                    </div>
                </div>
            </div>
            <div class="td order-1 order-lg-1 cursor-pointer name font-weight-bold text-truncate"
                 :title="campName ">
                <div class="text-truncate">{{ campName }}</div>
            </div>
            <div class="td order-4 order-lg-2">
                <day-picker width="33px"
                            height="22px"
                            :readonly="true"
                            :value="daysList">
                </day-picker>
            </div>
            <div class="td order-3 order-lg-3 cursor-pointer text-truncate"
                 :title="location">
                <span class="mobile-title mr-2"><img class="location-img" src="../../../../assets/img/map-pin.png"></span>
                <div class="text-truncate">{{ location }}</div>
            </div>
            <div class="td order-2 order-lg-4">
                <div>{{ localStartDate }}</div>
                <div v-if="showMobileVersion" class="delimiter mx-1"> - </div>
                <div>{{ localEndDate }}</div>
            </div>
            <div v-if="!showMobileVersion"
                 class="td order-lg-5">
                <div class="d-flex justify-content-between align-items-center">
                    <img v-if="pricings.length"
                         src="../../../../assets/img/opened.svg"
                         :class="['down-arrow', isPopupOpen ? 'rotatedBack': 'rotated']"
                         @click="togglePopup">
                    <options class="ml-auto"
                             v-if="!readonly"
                             :options="accessibleItems">
                    </options>
                </div>
            </div>
        </div>
       <div>
           <camp-pricings-list v-if="isPopupOpen"
                               :readonly="readonly"
                               :camp-id="campId"
                               :pricings="pricings">
           </camp-pricings-list>
       </div>
    </div>
</template>

<script>
    import * as Routes from '@/router/routes';
    import { mapActions, mapGetters } from 'vuex';
    import { Confirmation } from '@/store/modules/confirm/Confirmation';
    import SelectDirective from '@/directives/SelectDirective';
    import { FormatDatesMixin } from '@/mixins/FormatDatesMixin';
    import { ManagePopupMixin } from '@/mixins/ManagePopupMixin';
    import { PermissionMixin } from '@/mixins/PermissionMixin';
    import { CAMP_OPTIONS } from './camp.options.config';
    import { saveToClipboard } from '@/common/utils/clipboard';
    import { ResizeMobileMixin } from '@/mixins/ResizeMobileMixin';

    export default {
        name: 'CampRow',
        components: {
            CampPricingsList: () => import(/* webpackChunkName: "camps" */ './CampPricingsList'),
            Options:    () => import('@/components/popup/Options'),
            DayPicker:  () => import('@/components/controls/DayPicker'),
            Checkbox:   () => import('@/components/controls/Checkbox')
        },
        props: {
            campId: {
                type: Number,
                required: true
            },
            readonly: {
                type: Boolean,
                default: false
            },
            campName: {
                type: String,
                required: true
            },
            location: {
                type: String,
                required: true
            },
            gradeTo: {
                type: String
            },
            gradeFrom: {
                type: String
            },
            ageTo: {
                type: String
            },
            ageFrom: {
                type: String
            },
            daysList: {
                type: Array
            },
            startDate: {
                type: String
            },
            endDate: {
                type: String
            },
            pricings: {
                type: Array
            }
        },
        data() {
            return {
                hideScroll: false,
            }
        },
        directives: { SelectDirective },
        mixins: [FormatDatesMixin, ManagePopupMixin, PermissionMixin(CAMP_OPTIONS), ResizeMobileMixin(992)],
        computed: {
            ...mapGetters([
                'currencySign',
                'selected',
                'portalName'
            ]),
            checked() {
                return this.selected.includes(this.campId);
            },
            range() {
                let rangeFrom, rangeTo, type;
                if (this.gradeFrom) {
                    rangeFrom = GRADES.find(el => el.data === this.gradeFrom).view;
                    rangeTo = GRADES.find(el => el.data === this.gradeTo).view;
                    type = 'Grade';
                }
                else {
                    rangeFrom = AGES.find(el => el.data === this.ageFrom).view;
                    rangeTo = AGES.find(el => el.data === this.ageTo).view;
                    type = 'Age';
                }
                return rangeFrom === rangeTo ?
                    rangeFrom :
                    `${rangeFrom} - ${rangeTo}`;
            }
        },
        methods: {
            ...mapActions([
                'openConfirmDialog',
                'createSuccessNotification',
                'createErrorNotification',
                'openModal',
                'prefillCampForm',
                'deleteCamp',
                'copyLinkCamp'
            ]),
            async openCampFormForEdit() {
                this.$router.push({ name: Routes.CAMP_EDIT_FORM, params: { id: this.campId } });
            },
            confirmDeleteCamp() {
                this.openConfirmDialog(new Confirmation(
                    () => this.deleteCamp(this.campId)
                        .then(() => this.createSuccessNotification('You have removed a camp'))
                ));
            },

            copyLink() {
                let search = this.campName.replace('%', '%25').replace('#', '%23').replace('&', '%26');

                //create link
                const link = `${window.location.origin}/parents/${this.portalName}/activities?activityType=CampsList&search=${search}`;

                // check Safari
                const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) {
                    return p.toString() === "[object SafariRemoteNotification]";
                })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

                // check Internet Explorer
                const isIE = /*@cc_on!@*/false || !!document.documentMode;

                // Scroll to top
                window.addEventListener('scroll', window.scrollTo(0, 0));

                // Copy to Clipboard
                if (isIE) { // Internet Explorer
                    window.clipboardData.setData('Text', link);
                    this.createSuccessNotification('Copying to clipboard was successful!');
                } else {  //  Chrome, Opera, Edge, Firefox
                    saveToClipboard(link);
                    this.createSuccessNotification('Copying to clipboard was successful!');
                }

                window.removeEventListener('scroll', window.scrollTo(0, 0));
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../assets/colors';
    @import '../../../../assets/mixins';

    @include table-tr-custom-mobile();

    .down-arrow {
        width: 48px;
        height: 48px;
        cursor: pointer;
        margin-right: 3px;
        border-radius: 50%;
    }

    .rotated {
        @include rotate(0.2s, 360deg);
    }

    .rotatedBack {
        @include rotate(0.2s, -180deg);
    }

    .shadow {
        box-shadow: 0 2px 40px 0 rgba(37, 31, 72, 0.14);
    }

    .camp-row.table-row {
        height: 96px;
        margin-bottom: 0;

        .td {
            &:nth-child(2) {
                font-weight: bold;
                font-size: 16px;
                color: $SoftBlue;
            }
        }

        @media screen and (max-width: 991px) {
            padding-bottom: 10px;

            .td {
                margin-bottom: 10px;
            }

            .tools {
                /deep/ .more {
                    background: url('../../../../assets/img/more.svg') no-repeat center;
                }

                .down-arrow {
                    cursor: pointer;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    margin-right: 3px;
                }
            }

            .name {
                font-size: 23px !important;
            }

            img {
                height: 13px;
                width: 10px;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/camps/CampRow.vue